import {
  EventStructuresFilters,
  MySalesHistory,
  OrganiserStructureType,
  OrganiserStructureWithProductsType,
  OrganiserSuggestionType,
  OrganiserWithEvents,
  PaginatedResponse,
  PendingPrestationOrdersResponse,
  QueryArgs,
  Visitor,
} from "@ac-expo/api-types";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import {
  organiserExhibitorsQueryKeys,
  organisersQueryKeys,
  organiserVisitorsQueryKeys,
} from "../query-keys/organiser.query-keys";
import userQueryKeys from "../query-keys/user.query-keys";

const getOrganiserExhibitors = async () => {
  const { data } = await privateAxios.get<OrganiserStructureType[]>(
    "/organiser/exhibitors"
  );
  return data;
};

export const useGetOrganiserExhibitorsQuery = () => {
  return useQuery({
    queryKey: organiserExhibitorsQueryKeys.me(),
    queryFn: () => getOrganiserExhibitors(),
  });
};

const getEventStructures = async (eventId: string) => {
  const { data } = await privateAxios.get<OrganiserStructureWithProductsType[]>(
    `/organiser/events/${eventId}`
  );
  return data;
};

export const useGetEventStructuresQuery = (eventId: string) => {
  return useQuery({
    queryKey: organiserExhibitorsQueryKeys.event(eventId),
    queryFn: () => getEventStructures(eventId),
  });
};

const getEventStructuresPaginated = async (
  eventId: string,
  filters?: EventStructuresFilters
) => {
  const { data } = await privateAxios.get<
    PaginatedResponse<OrganiserStructureWithProductsType>
  >(`/organiser/events/${eventId}/paginated`, {
    params: filters,
  });
  return data;
};

export const useGetEventStructuresPaginatedQuery = (
  eventId: string,
  filters?: EventStructuresFilters,
  options?: QueryArgs<PaginatedResponse<OrganiserStructureWithProductsType>>
) => {
  return useQuery({
    queryKey: organiserExhibitorsQueryKeys.eventStructuresPaginated(
      eventId,
      filters
    ),
    queryFn: () => getEventStructuresPaginated(eventId, filters),
    ...options,
  });
};

export const getOrganiserVisitors = async () => {
  try {
    const { data } = await privateAxios.get<Visitor[]>("/organiser/visitors");
    return data;
  } catch (error) {
    if (isAxiosError(error)) error.message = error.response?.data.message;
    throw error;
  }
};

export const useGetOrganiserVisitorsQuery = <TData = Visitor[]>(
  options?: QueryArgs<Visitor[], TData>
) => {
  return useQuery({
    queryKey: organiserVisitorsQueryKeys.me(),
    queryFn: getOrganiserVisitors,
    ...options,
  });
};

const getAutoCompleteSuggestions = async (
  searchText: string,
  useName = false
) => {
  const res = await privateAxios.get<OrganiserSuggestionType[]>(
    "/organiser/autocomplete",
    {
      params: {
        searchText,
        useName,
      },
    }
  );
  return res.data;
};

export const useGetOrganisersAutoCompleteSuggestions = (
  searchText: string,
  useName = false,
  enabled = true
) => {
  return useQuery({
    queryKey: organiserVisitorsQueryKeys.autocomplete(searchText, useName),
    queryFn: () => getAutoCompleteSuggestions(searchText, useName),
    enabled: !!searchText && enabled,
  });
};

const getMyOrganiserSalesHistory = async () => {
  const { data } = await privateAxios.get<MySalesHistory[]>(
    "/organiser/sales-history"
  );
  return data;
};

export const useMyOrganiserSalesHistoryQuery = () => {
  return useQuery({
    queryKey: userQueryKeys.salesHistory(),
    queryFn: getMyOrganiserSalesHistory,
    select: (data) =>
      data.map((sale) => ({
        ...sale,
        date: new Date(sale.date),
      })),
  });
};

const getOrganisersWithEvents = async () => {
  const { data } = await privateAxios.get<OrganiserWithEvents[]>(
    "/organiser/with-events"
  );
  return data;
};

export const useGetOrganisersWithEventsQuery = () => {
  return useQuery({
    queryKey: organisersQueryKeys.withEvents(),
    queryFn: getOrganisersWithEvents,
  });
};

const checkPendingPrestationOrders = async (): Promise<PendingPrestationOrdersResponse> => {
  const { data } = await privateAxios.get<PendingPrestationOrdersResponse>(
    "/organiser/pending-prestation-orders"
  );
  return data;
};

export const useHasPendingPrestationOrdersQuery = (
  options?: QueryArgs<PendingPrestationOrdersResponse>
) => {
  return useQuery({
    queryKey: organisersQueryKeys.pendingPrestationOrders(),
    queryFn: checkPendingPrestationOrders,
    ...options,
  });
};
